import { RankingTabs } from './RankingTabs';
import { SeasonRankCategories, fetchSeasonState } from './SeasonRankPanel';
import '@/styles/ranking-layout.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useRanking, getRankingErrorKey, type RankingPlayerRow } from '@/hooks/useRanking';
import { characterAvatarUrl, raceImageUrl, assetUrl } from '@/lib/assetUrl';
import { useQueryClient } from '@tanstack/react-query';
import { fetchRanking } from '@/hooks/useRanking';
import { RANKING_TABS, formatGold, type RankingCategory } from './rankingTypes';
import { RankingPagination } from './RankingPagination';
import {
  MASTERY_BY_RACE,
  filterRankingRows,
  getRaceFromRefObjId,
  paginateRows,
  totalPages,
  type RaceFilter,
} from './rankingUtils';

const DEFAULT_ICON = assetUrl('/assets/images/character/icon/14875.webp');
const DEFAULT_RACE = assetUrl('/assets/images/character/race_china.webp');

function RankingRow({ row, category, onClick }: { row: RankingPlayerRow; category: RankingCategory; onClick: (id: number) => void }) {
  const race = getRaceFromRefObjId(Number(row.RefObjID));

  return (
    <tr
      className="ranking-row"
      data-charid={row.CharID}
      data-race={race}
      data-mastery={row.MasteryName}
      onClick={() => onClick(row.CharID)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(row.CharID); }
      }}
    >
      <td>{row.Rank}</td>
      <td className="ranking-character-cell">
        <img
          src={characterAvatarUrl(row.RefObjID)}
          alt=""
          className="ranking-char-icon"
          onError={(e) => {
            e.currentTarget.src = DEFAULT_ICON;
          }}
        />
        <span className="ranking-char-level">Lv.{row.CurLevel}</span>
        <img
          src={raceImageUrl(Number(row.RefObjID))}
          alt=""
          className="ranking-race-icon"
          onError={(e) => {
            e.currentTarget.src = DEFAULT_RACE;
          }}
        />
        <span className="ranking-char-name">{row.CharName16}</span>
        <span className="ranking-char-mastery">| {row.MasteryName}</span>
      </td>
      <td>{row.GuildName}</td>
      <td className="ranking-points" title={row.Score}>{category === 'gold' ? formatGold(row.Score ?? '0') : row.Score ?? row.ItemPoints}</td>
    </tr>
  );
}

export function RankingPage({ category = 'player' }: { category?: RankingCategory }) {
  const queryClient = useQueryClient();
  const ranking = RANKING_TABS.find((entry) => entry.slug === category)!;
  const { t } = useLocale();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refreshRanking, isFetching } = useRanking(ranking.type, 500);

  const [seasonFooter, setSeasonFooter] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [race, setRace] = useState<RaceFilter>('');
  const [mastery, setMastery] = useState('');
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!data) return;
    void queryClient.prefetchQuery({queryKey:['season-rank-state'],queryFn:fetchSeasonState,staleTime:15000});
    // Warm the other URLs without delaying the visible ranking.
    for (const entry of RANKING_TABS) {
      if (entry.type === ranking.type) continue;
      void queryClient.prefetchQuery({
        queryKey: ['ranking', entry.type, 500],
        queryFn: () => fetchRanking(entry.type, 500),
        staleTime: 120_000,
      });
    }
  }, [Boolean(data), ranking.type, queryClient]);

  const rows = data?.data ?? [];

  const filtered = useMemo(
    () => filterRankingRows(rows, search, race, mastery),
    [rows, search, race, mastery],
  );

  const pages = totalPages(filtered.length);
  const pageRows = useMemo(() => paginateRows(filtered, page), [filtered, page]);

  const masteryOptions = race ? MASTERY_BY_RACE[race] : [];

  const handleRaceChange = useCallback((value: RaceFilter) => {
    setRace(value);
    setMastery('');
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setSearch('');
    setRace('');
    setMastery('');
    setPage(1);
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    handleReset();
    try {
      await refreshRanking();
    } finally {
      setRefreshing(false);
    }
  }, [handleReset, refreshRanking]);

  const goProfile = useCallback(
    (charId: number) => {
      navigate(`/ranking/character-profile/${charId}?from=${category}`);
    },
    [navigate, category],
  );

  return (
    <div className="container-ingame fade-in">
      <div className="calendar-container ranking-page player-ranking-page">
        <SimpleMenu title={t('ranking')}>
          <RankingTabs active={category} />
        </SimpleMenu>

        <div className="calendar-info-container">
          {category === 'season' ? <SeasonRankCategories footer={seasonFooter} /> : (
          <div className="ranking-filters-container">
            <div className="ranking-filters">
              <div className="ranking-filter-group">
                  <input
                    type="text"
                    id="ranking-search" aria-label={t('ranking_search_placeholder')}
                    className="ranking-filter-input"
                    placeholder={t('ranking_search_placeholder')}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="ranking-filter-group">
                  <select
                    id="ranking-filter-race" aria-label={t('ranking_filter_race')}
                    className="ranking-filter-select browser-default"
                    value={race}
                    onChange={(e) => handleRaceChange(e.target.value as RaceFilter)}
                  >
                    <option value="">{t('ranking_filter_race')}</option>
                    <option value="chinese">{t('ranking_race_chinese')}</option>
                    <option value="euro">{t('ranking_race_euro')}</option>
                  </select>
                </div>
                <div className="ranking-filter-group">
                  <select
                    id="ranking-filter-mastery" aria-label={t('ranking_filter_mastery')}
                    className="ranking-filter-select browser-default"
                    value={mastery}
                    disabled={!race}
                    onChange={(e) => {
                      setMastery(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">{t('ranking_filter_mastery')}</option>
                    {masteryOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(m.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  id="ranking-reset-btn" aria-label={t('ranking_filter_refresh')}
                  className={`ranking-filter-btn ranking-filter-btn-reset${refreshing || isFetching ? ' refreshing' : ''}`}
                  title={t('ranking_filter_refresh')}
                  onClick={() => void handleRefresh()}
                  disabled={refreshing || isFetching}
                >
                <i className="fa fa-undo" aria-hidden />
              </button>
            </div>
          </div>

          )}
          <div className="ranking-results-layout">
          <div className="ranking-table-section">
            <div className="log-table-wrapper ranking-table-wrapper">
              <table id="ranking-table" className="ranking-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('ranking_col_character')}</th>
                    <th>{t('ranking_col_guild')}</th>
                    <th>{category === 'gold' ? 'Gold' : t('ranking_col_points')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={4}>{t('loading_data')}</td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td colSpan={4}>{t(getRankingErrorKey(error))}</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4}>{t('ranking_empty')}</td>
                    </tr>
                  ) : pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={4}>{t('ranking_no_results')}</td>
                    </tr>
                  ) : (
                    pageRows.map((row) => (
                      <RankingRow key={row.CharID} row={row} category={category} onClick={goProfile} />
                    ))
                  )}
                </tbody>
              </table>
              <div ref={category === 'season' ? setSeasonFooter : undefined} className={`ranking-table-footer${category === 'season' ? ' season-footer' : ''}`}>
                <RankingPagination page={page} pages={pages} onPageChange={setPage} />
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
